import { datadogLogs } from "@datadog/browser-logs";

/**
 * List of third-party services that commonly cause non-critical fetch errors
 * These errors should be logged as info/debug instead of errors
 */
export const THIRD_PARTY_SERVICES = [
    // Google services
    'google.com',
    'www.google.com',
    'googleadservices.com',
    'www.googleadservices.com',
    'google-analytics.com',
    'www.google-analytics.com',
    'googletagmanager.com',
    'www.googletagmanager.com',
    'googletagservices.com',
    'doubleclick.net',
    'googlesyndication.com',
    'translate.googleapis.com',

    // Google patterns spécifiques
    'pagead/conversion',
    'pagead/ads',
    'measurement/conversion',

    // Facebook services
    'facebook.com',
    'www.facebook.com',
    'privacy_sandbox',
    'topics/registration',

    // Common error messages that are not critical
    'canceled'
];

/**
 * Check if an error comes from a third-party service
 * This function can be used by other modules (e.g., DataDog) to maintain consistency
 */
export function isThirdPartyError(error: Error | string): boolean {
    // message is not always a string: transformed errors from the axios
    // interceptor carry a bilingual {fr, en} object — coerce safely. This runs
    // inside the global error handlers, so it must never throw itself (a
    // null-prototype object or a broken custom toString would otherwise turn
    // the filter into a new uncaught error).
    const rawMessage = typeof error === 'string' ? error : error?.message;
    let errorMessage = '';
    if (typeof rawMessage === 'string') {
        errorMessage = rawMessage;
    } else if (typeof error === 'object' && error !== null) {
        try {
            errorMessage = String(error);
        } catch {
            errorMessage = Object.prototype.toString.call(error);
        }
    }

    // Convert to lowercase for case-insensitive matching
    const lowerErrorMessage = errorMessage.toLowerCase();

    // Check if error contains any third-party service
    if (THIRD_PARTY_SERVICES.some(service =>
        lowerErrorMessage.includes(service.toLowerCase()) ||
        (typeof error === 'object' && error?.stack?.toLowerCase().includes(service.toLowerCase()))
    )) {
        return true;
    }

    // Check for exact matches with common error messages
    if (lowerErrorMessage === 'canceled' || lowerErrorMessage === 'cancelled') {
        return true;
    }

    // Check for fetch errors ONLY with specific third-party patterns
    if (lowerErrorMessage.includes('fetch error')) {
        // Only filter known third-party services, not all fetch errors
        return (
            lowerErrorMessage.includes('googleadservices.com') ||
            lowerErrorMessage.includes('facebook.com') ||
            lowerErrorMessage.includes('google.com/measurement') ||
            lowerErrorMessage.includes('pagead') ||
            lowerErrorMessage.includes('googlesyndication.com') ||
            lowerErrorMessage.includes('doubleclick.net') ||
            lowerErrorMessage.includes('googletagmanager.com')
        );
    }

    return false;
}

/**
 * Handle unhandled promise rejections (fetch errors, etc.)
 */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const error = event.reason;

    if (isThirdPartyError(error)) {
        // Log as info instead of error for third-party services
        console.info('Third-party service unavailable (likely blocked):', error);

        // Optional: Log to DataDog as info for monitoring trends
        try {
            datadogLogs.logger.info('Third-party service fetch failed', {
                error: typeof error === 'string' ? error : error?.message,
                type: 'third_party_fetch_error',
                eventId: sessionStorage.getItem("claims_portal_event_id")
            });
        } catch (logError) {
            // Fallback if DataDog is not available
            console.info('Failed to log third-party error to DataDog:', logError);
        }

        // Prevent the error from being logged as unhandled
        event.preventDefault();
        return;
    }

    // For non-third-party errors, let them be handled normally
    // This ensures real application errors are still caught and logged
}

/**
 * Handle window errors (JavaScript errors)
 */
function handleWindowError(event: ErrorEvent): void {
    const error = event.error || event.message;

    if (isThirdPartyError(error)) {
        // Log as info instead of error for third-party services
        console.info('Third-party script error (likely blocked):', error);

        // Optional: Log to DataDog as info for monitoring trends
        try {
            datadogLogs.logger.info('Third-party script error', {
                error: typeof error === 'string' ? error : error?.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                type: 'third_party_script_error',
                eventId: sessionStorage.getItem("claims_portal_event_id")
            });
        } catch (logError) {
            // Fallback if DataDog is not available
            console.info('Failed to log third-party script error to DataDog:', logError);
        }

        // Prevent the error from being logged as unhandled
        event.preventDefault();
        return;
    }

    // For non-third-party errors, let them be handled normally
}

/**
 * Filter console errors for third-party services
 * This prevents third-party errors from cluttering the console
 */
function filterConsoleErrors(): void {
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    const originalConsoleLog = console.log;

    console.error = (...args: any[]) => {
        const message = args.join(' ');
        if (isThirdPartyError(message)) {
            // Log as info instead of error for third-party services
            console.info('Third-party service error (filtered):', ...args);
            return;
        }
        originalConsoleError.apply(console, args);
    };

    console.warn = (...args: any[]) => {
        const message = args.join(' ');
        if (isThirdPartyError(message)) {
            // Log as info instead of warn for third-party services
            console.info('Third-party service warning (filtered):', ...args);
            return;
        }
        originalConsoleWarn.apply(console, args);
    };

    console.log = (...args: any[]) => {
        const message = args.join(' ');
        if (isThirdPartyError(message)) {
            // Log as info for third-party services
            console.info('Third-party service log (filtered):', ...args);
            return;
        }
        originalConsoleLog.apply(console, args);
    };
}

/**
 * Setup global error handlers to filter third-party service errors
 * Should be called once during application initialization
 */
export function setupThirdPartyErrorFiltering(): void {
    // Handle unhandled promise rejections (fetch errors, async errors)
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Handle JavaScript errors
    window.addEventListener('error', handleWindowError);

    // Filter console errors
    filterConsoleErrors();

    console.info('Third-party error filtering enabled for:', THIRD_PARTY_SERVICES);
}

/**
 * Cleanup error handlers (useful for testing or hot reload)
 */
export function cleanupThirdPartyErrorFiltering(): void {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    window.removeEventListener('error', handleWindowError);
}
